// ADR 10.9.26h — the phones' capture names, and nothing the phones do not send.
//
// Four events, all already read or already declared by the native clients, so
// the web joins existing series rather than minting a fourth spelling of each:
//
//   • `vocab_stream_started` — `VocabViewModel.kt:146`, fired at the generate
//     tap. The name is historical: Android fires it on the *structured* path
//     too, and it is the first step of `conversion-funnel.toml`. The web makes
//     no stream (§1) and still fires it, because a funnel step is `{ event }`
//     and a web tap that reported under a different name would fall out of the
//     funnel rather than into it.
//   • `vocab_generated` — `VocabFetcher.kt:72`, on a resolved response.
//   • `vocab_feedback_submitted` — `AnalyticsServiceVocabFeedback.kt:20`, when
//     a thumb *sets* a rating; withdrawals emit nothing, as on the phones.
//   • `flashcard_created` — `AnalyticsServiceFlashcard.kt:7`, read by
//     `feature-usage.toml` and `conversion-funnel.toml`.
//
// ADR 11.9.26j §3/§4 adds the two walls' impressions, again under names the
// phones already send:
//
//   • `account_prompt_shown { trigger: 'vocab_signed_out' }` — the stranger
//     who reaches `/vocab` and meets the sign-in slot. The ninth surface that
//     asks for an account (31.8.26i D1, 2.9.26g D1), read by
//     `lifecycle-account-prompt-shown-by-trigger` as a new row. The trigger is
//     NOT a `rust-core` value: the web calls no `limit_prompt_trigger` fold,
//     and widening `LimitSurface` for a value no phone can reach would shift a
//     UniFFI checksum for nothing.
//   • `upgrade_prompt_shown { trigger: 'vocab_limit' }` — the learner's door
//     on the 429 cap sheet, under the phones' own value for the same wall, so
//     `tutor-funnel-upgrade-prompts-by-trigger` pools all three clients'
//     learners on one row. Never `upgrade_prompt_suppressed`: the sheet is a
//     wall with a door, not a pitch under 10.9.26e's throttle.
//   • `account_prompt_shown { trigger: 'vocab_limit' }` — the stranger's door
//     on the same sheet (ADR 3.10.26b §3): the phones' `CreateAccount` arm, a
//     different event on the same trigger, as `limit_prompt_funnel` prescribes.
//   • `upgrade_prompt_shown { trigger: 'vocab_wall' }` — the learner's door
//     beneath the empty wall, `limit_upgrade_button` (Infra ADR 15.9.26a §4).
//     A NEW trigger value, not `vocab_limit`: a wall is not a 429, and an
//     empty wall is already inside `vocab_generated`. The by-trigger reader
//     breaks down on `trigger` unfiltered, so it is a new row with no Infra
//     edit. The vocab triggers are a CLOSED SET, pinned by
//     `__tests__/analytics.test.ts`: a door added dark fails a test.
//
// ADR 11.9.26i §2 adds the one event that is the web's own, because the moment
// it measures is the web's own:
//
//   • `device_enrolled { difficulty, solve_ms, renewal }` — a browser minted or
//     renewed its device token. The phones enrol in the background behind a
//     legacy header and never wait; the browser has no header to send
//     meanwhile, so its first anonymous request *awaits* the solve, and
//     `solve_ms` is the number that bet is graded on (§2, Consequences).
//     Consent-gated through `telemetry/capture.ts` like every other event, and
//     never carrying the token: a server-signed credential does not belong in
//     a third party's event store (§3).
//
// ADR 15.9.26aa D1 adds the one event that fires when the generation does not
// resolve, again under the phones' name:
//
//   • `vocab_generate_failed { reason, status, hsk_level, source_lang,
//     target_lang }` — `POST /vocab` failed, once per request the learner saw
//     fail. `reason` is rust-core's `api_error_label` vocabulary, which the
//     phones call through the FFI and the web derives from the status here
//     (`vocabFailureReason`, in `failureReason.ts` since 15.9.26ad took this
//     file to its line budget) — twelve of the thirteen slugs (`timeout`
//     since 2.10.26s gave every fetch a ceiling); it never sees
//     `generation_stream_unavailable` (stream-only). Pinned to the phones'
//     spellings by `__tests__/analytics.test.ts`. Fires on a 429 **in
//     addition to** the cap sheet's door (either arm): that one counts a
//     wall shown, this one counts a request that failed.
//
// ADR 15.9.26ad D1/D2 adds the padlock's two pairs and the reset, under the
// phones' names and — the part that matters — at the phones' *moment*:
//
//   • `upgrade_prompt_shown` / `upgrade_prompt_converted`
//     `{ trigger: 'locked_language', target: 'subscription_screen' }` and
//     `account_prompt_shown` / `account_prompt_converted`
//     `{ trigger: 'signin_language', target: 'auth_screen' }` — the click on a
//     door, never a mount. On the phones `shown` is the padlock *tap*; the
//     padlocks here render on every free visit to two pages, so a
//     mount-time `shown` would pool a page-view count under a tap count and
//     make `locked_language` the funnel's largest trigger with a conversion
//     rate that means nothing. `shown` is latched once per page session per
//     state (the phones' `promptShownOnce`); `converted` fires every click —
//     no throttle, the wall door's stance (15.9.26a §4), so the web's ratio
//     is 1 where the phones' throttle can hold `converted` back.
//   • `language_target_reset { from, to, platform: 'web' }` — `useTargetReset`
//     applied a `Some`: one event per edge, codes never names.
//
// **Every name and property key is a constant here** (`features/shared/analytics.ts`'s
// rule): a typo is a type error, not a new series. No item text, no example
// sentence, no translation reaches a property — generated content is not
// telemetry.

import { capture } from '../../telemetry/capture'
import { BILLING_EVENTS } from '../billing/analytics'
import { vocabFailureReason } from './failureReason'

export { vocabFailureReason, type VocabFailure, type VocabFailureReason } from './failureReason'

/** Where an event goes. Injected so the suite can watch one spy (6.9.26l). */
export type AnalyticsSink = (name: string, properties?: Record<string, unknown>) => void

export const VOCAB_EVENTS = {
  requested: 'vocab_stream_started',
  generated: 'vocab_generated',
  feedbackSubmitted: 'vocab_feedback_submitted',
  flashcardCreated: 'flashcard_created',
  accountPromptShown: 'account_prompt_shown',
  // Billing's name, not a second spelling of it (11.9.26j §4).
  upgradePromptShown: BILLING_EVENTS.promptShown,
  deviceEnrolled: 'device_enrolled',
  generateFailed: 'vocab_generate_failed',
  // 15.9.26ad D1 — names the web had never sent, declared once so there is
  // one spelling; D2's reset event is the insight test in Infra's literal.
  upgradePromptConverted: 'upgrade_prompt_converted',
  accountPromptConverted: 'account_prompt_converted',
  languageTargetReset: 'language_target_reset',
} as const

/**
 * The signed-out slot's `trigger` — in `ACCOUNT_TRIGGER`'s shape
 * (`billing/analytics.ts`). Not `sign_in`: that names the auth screen itself,
 * and a stranger who arrives there from this slot is counted there too.
 */
export const VOCAB_SIGNED_OUT_TRIGGER = 'vocab_signed_out'

/** The cap sheet's `trigger` — the phones' value for the same wall. */
export const VOCAB_LIMIT_TRIGGER = 'vocab_limit'

/**
 * The learner's wall door's `trigger` (15.9.26a §4). Its own value because a
 * wall is not a 429, and an empty wall is already inside `vocab_generated`
 * (`useVocabGeneration.ts` fires it on every resolved body, `item_count: 0`
 * included).
 */
export const VOCAB_WALL_TRIGGER = 'vocab_wall'

/**
 * The padlock's two triggers (15.9.26ad D1) — 15.9.26h D4's strings verbatim,
 * so the web joins the phones' series rather than starting a fourth. Unlike
 * the three above these are NOT web-minted: `analytics.test.ts`'s closed set
 * widened from three to five to admit them, with that distinction as the reason.
 */
export const LOCKED_LANGUAGE_TRIGGER = 'locked_language'
export const SIGNIN_LANGUAGE_TRIGGER = 'signin_language'

/** The two door states — `LanguageRowState` less `'selectable'`, which has no door. */
export type LockedDoorState = 'locked_upgrade' | 'locked_sign_in'

/**
 * The `shown` latch: once per page session per state. Module-level, not a
 * `useRef` — `features/shared/analytics.ts` §2's reason: React 18 StrictMode
 * mounts twice in development, and a per-mount latch would report one click
 * as two exactly where the implementer is watching.
 */
const doorShown = new Set<LockedDoorState>()

const defaultSink: AnalyticsSink = (name, properties) => {
  capture(name, properties)
}

let sink: AnalyticsSink = defaultSink

/** Test seam: watch the events without attaching an SDK. */
export function setVocabSink(replacement: AnalyticsSink): void {
  sink = replacement
}

export function resetVocabSink(): void {
  sink = defaultSink
}

/** Test seam: a fresh page session for the door latch. */
export function resetLockedDoorLatch(): void {
  doorShown.clear()
}

/**
 * The locked-target door was clicked. Fires the phones' pair in the phones'
 * order: `*_prompt_shown` latched once per page session per state, then
 * `*_prompt_converted` every time — `lockedLanguageHandler`'s sequence.
 */
export function lockedLanguageDoorFollowed(state: LockedDoorState): void {
  const [shown, converted, trigger, target] =
    state === 'locked_upgrade'
      ? [VOCAB_EVENTS.upgradePromptShown, VOCAB_EVENTS.upgradePromptConverted, LOCKED_LANGUAGE_TRIGGER, 'subscription_screen']
      : [VOCAB_EVENTS.accountPromptShown, VOCAB_EVENTS.accountPromptConverted, SIGNIN_LANGUAGE_TRIGGER, 'auth_screen']
  if (!doorShown.has(state)) {
    doorShown.add(state)
    sink(shown, { trigger })
  }
  sink(converted, { trigger, target })
}

/** `useTargetReset` applied a replacement — codes, never names, `platform: 'web'`. */
export function languageTargetReset(from: string, to: string): void {
  sink(VOCAB_EVENTS.languageTargetReset, { from, to, platform: 'web' })
}

/** The generate tap — `hsk_level` is the phones' property name, kept. */
export function vocabRequested(level: number, targetLang: string): void {
  sink(VOCAB_EVENTS.requested, { hsk_level: level, target_lang: targetLang })
}

export interface GeneratedProps {
  level: number
  sourceLang: string
  targetLang: string
  itemCount: number
  latencyMs: number
}

export function vocabGenerated(props: GeneratedProps): void {
  sink(VOCAB_EVENTS.generated, {
    hsk_level: props.level,
    source_lang: props.sourceLang,
    target_lang: props.targetLang,
    item_count: props.itemCount,
    latency_ms: props.latencyMs,
  })
}

export interface FailedProps {
  error: unknown
  level: number
  sourceLang: string
  targetLang: string
}

/**
 * `POST /vocab` rejected — fired beside `failureAction`, after the sequence
 * guard, so a superseded request's failure is not counted (the rule
 * `vocabGenerated` already follows). The property set is `vocab_generated`'s
 * plus `reason` and `status`; the server's sentence is rendered, never captured.
 */
export function vocabGenerateFailed(props: FailedProps): void {
  const { reason, status } = vocabFailureReason(props.error)
  sink(VOCAB_EVENTS.generateFailed, {
    reason,
    status,
    hsk_level: props.level,
    source_lang: props.sourceLang,
    target_lang: props.targetLang,
  })
}

/** Only when a thumb sets a rating — the caller has already dropped withdrawals. */
export function vocabFeedbackSubmitted(target: string, rating: number): void {
  sink(VOCAB_EVENTS.feedbackSubmitted, { target, rating })
}

export function flashcardCreated(sourceLang: string, targetLang: string, level: number): void {
  sink(VOCAB_EVENTS.flashcardCreated, { source_lang: sourceLang, target_lang: targetLang, level })
}

/**
 * The stranger's account door mounted — once per mount, the caller's ref holds
 * the latch. Under 10.9.26h that door was the generate slot; since 11.9.26i §7
 * a stranger generates, and the door is `limit_create_account` beneath the
 * wall. Named after the surface, not the event: `billing/analytics.ts` already
 * owns `upgradePromptShown()` with its trigger fixed, and a second function of
 * that name with a different fixed trigger is a trap for the next importer.
 */
export function signedOutSlotShown(): void {
  sink(VOCAB_EVENTS.accountPromptShown, { trigger: VOCAB_SIGNED_OUT_TRIGGER })
}

export interface EnrolledProps {
  difficulty: number
  solveMs: number
  renewal: boolean
}

/** A device token was minted (`renewal: false`) or renewed (`renewal: true`). */
export function deviceEnrolled(props: EnrolledProps): void {
  sink(VOCAB_EVENTS.deviceEnrolled, {
    difficulty: props.difficulty,
    solve_ms: props.solveMs,
    renewal: props.renewal,
  })
}

/** The learner's `CapSheet` door mounted — once per 429, since the page unmounts it between. */
export function capSheetShown(): void {
  sink(VOCAB_EVENTS.upgradePromptShown, { trigger: VOCAB_LIMIT_TRIGGER })
}

/** A stranger's `CapSheet` door mounted — the phones' `CreateAccount` arm on `vocab_limit`. */
export function capSheetAccountShown(): void {
  sink(VOCAB_EVENTS.accountPromptShown, { trigger: VOCAB_LIMIT_TRIGGER })
}

/**
 * The learner's `UpgradeDoor` mounted beneath the empty wall — once per mount
 * of the door, the caller's ref holds the latch (15.9.26a §4). The third door
 * on the vocab surface, and the one 11.9.26j left dark.
 */
export function wallUpgradeShown(): void {
  sink(VOCAB_EVENTS.upgradePromptShown, { trigger: VOCAB_WALL_TRIGGER })
}
